import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable, takeUntil } from 'rxjs';

import { BaseComponent } from './../../../core/components/base/base.component';
import { FeedbackService } from './../../../core/services/feedback.service';
import { FileObject, mapMediaToFileObject } from './../../../core/interfaces/file-object';
import {
    PatientDocumentRequestDto,
    PatientDocumentRequestService,
    PatientDocumentReturnedFileDto,
    activeReturnedFiles,
    activeValidationFiles,
    describeDocumentFailure,
    supersededReturnedFiles,
} from './../../../core/services/patient-document-request.service';

export interface DocumentReviewDialogData {
    /** `PatientDocumentRequests.id`. The dialog loads everything else itself. */
    requestId: string;
    /**
     * The practice this request belongs to — REQUIRED (`tasks/practice-documents.md` §32).
     *
     * The read is scoped to it in SQL, so the caller has to say which practice it means rather
     * than the server accepting any request the user could reach. Both openers know it: the
     * patient-file panel from the patient, the Inbox from the worklist the row came off.
     */
    groupId: string;
    patientId?: string | null;
    /** For the heading, so the dialog says who this is about before the row has loaded. */
    patientName?: string | null;

    /**
     * FLAGGING, supplied by the caller — and the reason it is a callback.
     *
     * A reviewer who opens a document, decides it needs somebody else's eyes, and wants to mark it
     * for attention had to close the dialog, find the row again and click the flag on it. Reported
     * 11 Sep 2026.
     *
     * A flag is an INBOX concept: it is keyed on `itemType` + `sourceId` and lives on
     * `InboxService`. This dialog is also opened from the patient-file documents panel, where no
     * such concept exists. Injecting `InboxService` here would make every caller of a shared
     * dialog drag in the Inbox, so the Inbox passes in what it already knows how to do instead,
     * and the panel passes nothing — which is exactly why the control is absent there.
     *
     * `toggle()` emits the NEW flagged state. The dialog holds no opinion about what flagging
     * means; it draws a button and shows what it is told.
     */
    flag?: {
        flagged: boolean;
        toggle: () => Observable<boolean>;
    };
}

export interface DocumentReviewDialogResult {
    /** True when a decision was actually taken, so the caller knows to re-read its list. */
    changed: boolean;
    /** What it became. Absent when nothing was decided. */
    reviewStatus?: 'accepted' | 'rejected' | 'validated';
}

/**
 * Reviewing a returned document — BOTH STAGES, in one dialog, from anywhere.
 *
 * `tasks/practice-documents.md` §26, `tasks/inbox.md` §11 phase 2.
 *
 * ── WHY THIS IS SHARED AND NOT A SECOND IMPLEMENTATION ───────────────────────────────
 *
 * Sign-off and validation used to live only inside `patient-document-list.component.ts`, welded
 * into a 65k-line patient-file panel. The Inbox needs the same two decisions from a different
 * screen, and the one thing this codebase repeatedly refuses to do is keep two copies of a rule:
 * the whole of `utils/patientRequestDelivery.js` exists because a duplicated dedupe window is what
 * `patient-forms.md` §1.3 records the consent feature paying for.
 *
 * So the decisions live here, and the panel and the Inbox are both callers. It takes a `requestId`
 * and NOT a loaded row, deliberately: `GET /patient-document-requests/:id` is the only endpoint
 * that serves the returned pages, their per-read signed URLs and the encrypted notes, and a caller
 * holding a stale list row must not be able to open a dialog that acts on it.
 *
 * ── THE ORDER OF THE TWO STAGES IS THE WORKFLOW ──────────────────────────────────────
 *
 * A clinician does not see Validate on a document nobody has checked is the right one. The server
 * refuses it too (`409 NOT_SIGNED_OFF`); this only keeps a button off the screen that would be
 * refused if pressed.
 *
 * ── `canSignOff` / `canValidate` ARE `!== false`, NEVER `=== true` ───────────────────
 *
 * A server that has not shipped the §26 permission lists sends neither flag, and reading absent as
 * "forbidden" would disable every review button in the product. They are not the enforcement —
 * that is in the two handlers, because a hidden button is a panel and not a permission.
 */
@Component({
    selector: 'app-document-review-dialog',
    templateUrl: './document-review-dialog.component.html',
    styleUrls: ['./document-review-dialog.component.scss'],
})
export class DocumentReviewDialogComponent extends BaseComponent implements OnInit {
    request: PatientDocumentRequestDto | null = null;
    loading = true;
    loadFailed = false;

    /** A write is in flight. Guards both stages — nothing may be decided twice. */
    sending = false;

    /** Stage one's rejection, revealed in place rather than in a second dialog. */
    rejecting = false;
    rejectNote = '';
    rejectNoteError: string | null = null;

    /** Stage two. `media` holds `FileObject`s, NOT `File`s — see `filesFromMedia`. */
    validationNote = '';
    validationMedia: FileObject[] = [];
    /** Stage two's rejection shares `validationNote`'s box; this says which button was pressed. */
    validationError: string | null = null;

    private galleryCache: { [key: string]: FileObject[] } = {};

    /* ── Flagging. Absent unless the caller supplied a context ───────────────────────── */

    /** Mirrors the caller's state, then tracks what `toggle()` reports. */
    flagged = false;

    /** One request at a time: the button is a toggle and a double-click would undo itself. */
    flagBusy = false;

    get canFlag(): boolean {
        return !!(this.data && this.data.flag);
    }

    /**
     * Flag or unflag, and say which it now is.
     *
     * The dialog stays OPEN. Flagging is an annotation, not a decision — a reviewer marks a
     * document for attention and carries on reading it — so this deliberately does not close or
     * report `changed`, which is reserved for sign-off and validation.
     */
    toggleFlag(): void {
        if (!this.data.flag || this.flagBusy) return;
        this.flagBusy = true;

        this.data.flag.toggle()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (flagged) => {
                    this.flagBusy = false;
                    this.flagged = flagged;
                },
                error: () => {
                    this.flagBusy = false;
                    this.showToastMessage('Could not update the flag. Please try again.');
                },
            });
    }

    constructor(
        private dialogRef: MatDialogRef<DocumentReviewDialogComponent>,
        private patientDocumentRequestService: PatientDocumentRequestService,
        @Inject(MAT_DIALOG_DATA) public data: DocumentReviewDialogData,
        fs: FeedbackService,
        md: MatDialog,
    ) {
        super(fs, md);
    }

    ngOnInit(): void {
        this.initFlagState();
        this.load();
    }

    /**
     * Reads the row fresh.
     *
     * The returned files' `url`s are SHORT-LIVED and minted per request (`mediaUrl` is stored
     * unsigned), so a row cached from a list would hand the gallery links that have already
     * expired.
     */
    /** Seeded from the caller so the button is correct on the first frame, not after a round trip. */
    private initFlagState(): void {
        this.flagged = !!(this.data.flag && this.data.flag.flagged);
    }

    private load(): void {
        this.loading = true;
        this.loadFailed = false;
        this.patientDocumentRequestService.loadRequestById(this.data.requestId, this.data.groupId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (result: any) => {
                    this.loading = false;
                    this.request = result && result.data ? result.data : null;
                    this.loadFailed = !this.request;
                    this.galleryCache = {};
                },
                error: () => {
                    this.loading = false;
                    this.loadFailed = true;
                },
            });
    }

    /* ── What this document is ──────────────────────────────────────────────────────────── */

    get documentName(): string {
        return (this.request && this.request.PracticeDocument && this.request.PracticeDocument.name)
            || 'this document';
    }

    get patientName(): string | null {
        return this.data.patientName || null;
    }

    /* ── What may be done ───────────────────────────────────────────────────────────────── */

    /**
     * `status === 'completed'` is load-bearing: a rejected-and-re-asked row is back on `sent`, and
     * offering Sign off there would let somebody sign off a document the patient has not sent yet.
     */
    get canReview(): boolean {
        const request = this.request;
        return !!request
            && request.status === 'completed'
            && (!request.reviewStatus || request.reviewStatus === 'awaiting_review')
            && request.canSignOff !== false;
    }

    get canValidate(): boolean {
        const request = this.request;
        return !!request
            && request.status === 'completed'
            && request.reviewStatus === 'accepted'
            && request.canValidate !== false;
    }

    get isValidated(): boolean {
        return !!this.request && this.request.reviewStatus === 'validated';
    }

    /**
     * Why there is nothing to do, in words — never a dialog with no buttons and no explanation.
     *
     * The three reasons are genuinely different and send a reader to three different places: wait
     * for the patient, wait for a colleague who is named on the document, or nothing is wrong at
     * all because it is already done.
     */
    get noActionReason(): string | null {
        const request = this.request;
        if (!request || this.canReview || this.canValidate) return null;
        if (this.isValidated) return 'This is already on the patient\'s clinical timeline.';
        if (request.status !== 'completed') {
            return 'Nothing has been returned yet, so there is nothing to review.';
        }
        if (request.reviewStatus === 'awaiting_review' && request.canSignOff === false) {
            return 'Only the people named in this document\'s settings can sign it off.'
                + ' Change who can in Settings > Documents.';
        }
        if (request.reviewStatus === 'accepted' && request.canValidate === false) {
            return 'This has been signed off and is waiting on one of the clinicians named in the'
                + ' document\'s settings to validate it.';
        }
        return 'There is nothing to do on this document right now.';
    }

    /* ── The pages ──────────────────────────────────────────────────────────────────────── */

    /**
     * The returned pages, as the gallery draws them.
     *
     * Memoised on the file ids and their urls because `mapMediaToFileObject` builds a NEW array of
     * NEW objects on every call, and an `*ngFor` over a fresh array every change-detection pass
     * tears down and rebuilds every tile.
     */
    get gallery(): FileObject[] {
        const request = this.request;
        if (!request) return [];
        const files = activeReturnedFiles(request).filter((file) => !!file.url);
        const key = files.map((f) => `${f.id}:${f.url || ''}`).join(',');
        if (!(key in this.galleryCache)) {
            this.galleryCache[key] = mapMediaToFileObject(files.map((file) => ({
                id: file.id,
                mediaUrl: file.url,
                mediaType: file.mediaType,
                mediaFileName: file.mediaFileName,
                displayIndex: file.displayIndex,
                patientId: request.patientId,
            }))) || [];
        }
        return this.galleryCache[key];
    }

    /**
     * The active files the gallery CANNOT draw, so that nothing the patient sent goes missing.
     *
     * Two kinds, and both have to reach a reviewer: a PDF (which `mapMediaToFileObject` filters
     * out of galleries — and since the return path converts, a surviving PDF means the conversion
     * FAILED), and a row with no `url` (which the server returns deliberately, so a reviewer
     * learns a page exists and cannot be opened rather than silently seeing one fewer page).
     *
     * The two lists partition the active files exactly.
     */
    get fallbackFiles(): PatientDocumentReturnedFileDto[] {
        if (!this.request) return [];
        const drawn = new Set(this.gallery.map((item) => item.url));
        return activeReturnedFiles(this.request)
            .filter((file) => !file.url || !drawn.has(file.url));
    }

    /** What a rejection superseded. Shown separately, NEVER mixed with the live pages. */
    get supersededFiles(): PatientDocumentReturnedFileDto[] {
        return supersededReturnedFiles(this.request);
    }

    /**
     * The clinician's OWN attachments, from a separate table and a separate person.
     *
     * Never merged with what the patient returned: on a clinical record, provenance is part of the
     * content.
     */
    get validationFiles(): PatientDocumentReturnedFileDto[] {
        return activeValidationFiles(this.request);
    }

    fileName(file: PatientDocumentReturnedFileDto, index: number): string {
        return file.mediaFileName || `Page ${index + 1}`;
    }

    /* ── Stage one ──────────────────────────────────────────────────────────────────────── */

    signOff(): void {
        if (!this.canReview || this.sending) return;
        this.submitReview({ reviewStatus: 'accepted' },
            `"${this.documentName}" was signed off. A clinician validates it next.`);
    }

    startReject(): void {
        if (!this.canReview) return;
        this.rejecting = true;
        this.rejectNote = '';
        this.rejectNoteError = null;
    }

    cancelReject(): void {
        this.rejecting = false;
        this.rejectNote = '';
        this.rejectNoteError = null;
    }

    /**
     * Reject, WITH A NOTE, and the note is required.
     *
     * A rejection with no reason is a rejection nobody can act on: the next thing that happens is
     * a resend, and whoever sends it has to be able to tell the patient what was wrong. That is
     * the entire mechanism §0 decision 3 puts in place of an automated check.
     */
    reject(): void {
        if (!this.canReview || this.sending) return;
        const note = (this.rejectNote || '').trim();
        if (!note) {
            this.rejectNoteError = 'Say what is wrong with it, so the patient knows what to send back.';
            return;
        }
        this.submitReview({ reviewStatus: 'rejected', reviewNote: note },
            `"${this.documentName}" was rejected. Resend it to ask the patient again.`);
    }

    private submitReview(
        payload: { reviewStatus: 'accepted' | 'rejected'; reviewNote?: string },
        successMessage: string,
    ): void {
        if (!this.request) return;
        this.sending = true;
        this.rejectNoteError = null;

        this.patientDocumentRequestService.reviewRequest(this.request.id, payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.sending = false;
                    this.showToastMessage(successMessage);
                    this.close(true, payload.reviewStatus);
                },
                error: (error: any) => {
                    this.sending = false;
                    const failure = describeDocumentFailure(error, this.documentName);
                    this.showToastMessage(failure.message);
                    // NOT_RETURNED means this copy is stale: somebody else rejected it and the
                    // server put the row back to `sent`. Re-read rather than leaving a decision on
                    // offer that the server will refuse again.
                    if (failure.code === 'NOT_RETURNED') this.load();
                },
            });
    }

    /* ── Stage two ──────────────────────────────────────────────────────────────────────── */

    /**
     * `app-file-upload` accumulates into its OWN array and emits the WHOLE of it on every pick,
     * not just the new items — so this REPLACES rather than appends. Appending would double every
     * attachment the moment a second was added, and the count is what decides whether a validation
     * may carry no observation.
     *
     * Deduped by `url`, so a re-emit of an unchanged array does not grow it.
     */
    onValidationFilesPicked(picked: FileObject[]): void {
        const seen = new Set<string>();
        this.validationMedia = (picked || []).filter((item) => {
            if (!item || !item.url || seen.has(item.url)) return false;
            seen.add(item.url);
            return true;
        });
    }

    onValidationFileDeleted(removed: FileObject): void {
        if (!removed) return;
        this.validationMedia = this.validationMedia.filter((item) => item.url !== removed.url);
        if (removed.url) URL.revokeObjectURL(removed.url);
    }

    onValidationFilesEdited(edited: FileObject[]): void {
        this.validationMedia = edited || [];
    }

    /**
     * The `File`s the request actually posts, built HERE and not on pick.
     *
     * `app-file-upload` hands over `FileObject`s whose `url` is a `blob:` URL — the original `File`
     * is gone — so each is fetched back into a blob and rebuilt with its own name and type.
     * Building them at submit rather than on pick is what lets the gallery keep drawing from those
     * URLs in the meantime.
     */
    private async filesFromMedia(): Promise<File[]> {
        const files: File[] = [];
        for (const item of this.validationMedia) {
            if (!item || !item.url) continue;
            const blob = await (await fetch(item.url)).blob();
            files.push(new File([blob], item.name || `attachment-${Date.now()}`,
                { type: item.type || blob.type || 'application/octet-stream' }));
        }
        return files;
    }

    /** §26 — put it on the timeline. */
    async validate(): Promise<void> {
        if (!this.request || !this.canValidate || this.sending) return;

        const note = (this.validationNote || '').trim();
        /*
         * NOTHING IS REQUIRED HERE - the server dropped `VALIDATION_NOTE_REQUIRED` on 11 Sep 2026
         * and this mirror went with it. A clinician who has read the returned pages and has
         * nothing to add can put them on the timeline as they are; the row still names the
         * document, the file count and the validator (`compileDocumentReturn`).
         *
         * `sendBack` below still requires its note, because a rejection has to tell the patient
         * what to do differently.
         */

        let files: File[];
        try {
            files = await this.filesFromMedia();
        } catch {
            this.validationError =
                'Those images could not be read from this device. Try attaching them again.';
            return;
        }

        this.sendValidation({ reviewStatus: 'validated', validationNote: note || null }, files,
            `"${this.documentName}" is on the timeline.`);
    }

    /**
     * §26 — send it back to the patient FROM STAGE TWO.
     *
     * The same consequences as a stage-one rejection, because it is the same server-side path: the
     * ask re-opens on the same row, the returned pages deactivate, and the practice resends the
     * invite. The note is required here for the reason it is required there.
     */
    sendBack(): void {
        if (!this.request || !this.canValidate || this.sending) return;
        const note = (this.validationNote || '').trim();
        if (!note) {
            this.validationError =
                'Say what is wrong with it, so the patient knows what to send back.';
            return;
        }
        this.sendValidation({ reviewStatus: 'rejected', reviewNote: note }, [],
            `"${this.documentName}" was sent back. Resend it to ask the patient again.`);
    }

    private sendValidation(payload: any, files: File[], successMessage: string): void {
        if (!this.request) return;
        this.sending = true;
        this.validationError = null;

        this.patientDocumentRequestService.validateRequest(this.request.id, payload, files)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.sending = false;
                    this.showToastMessage(successMessage);
                    this.close(true, payload.reviewStatus);
                },
                error: (error: any) => {
                    this.sending = false;
                    const failure = describeDocumentFailure(error, this.documentName);
                    this.validationError = failure.message;

                    /*
                     * ALL THREE MEAN SOMEBODY ELSE ACTED while this dialog was open, so the row is
                     * RE-READ rather than left as it was.
                     *
                     * The panel used to close the dialog here, which was right when there was a
                     * row behind it that would refresh. There is not: the Inbox opens this over a
                     * list that is about to be re-paged, and closing would take the reason for the
                     * refusal off the screen with it.
                     *
                     * So it stays open and reloads, which is what stage one already does on
                     * `NOT_RETURNED`. The buttons then reflect what the server actually holds —
                     * without the re-read the dialog would keep offering a decision it has just
                     * been told is impossible.
                     */
                    if (failure.code === 'NOT_SIGNED_OFF' || failure.code === 'ALREADY_VALIDATED'
                        || failure.code === 'NOT_RETURNED') {
                        this.load();
                    }
                },
            });
    }

    /* ── Closing ────────────────────────────────────────────────────────────────────────── */

    cancel(): void {
        this.close(false);
    }

    /**
     * The object URLs are revoked HERE and nowhere earlier: the gallery draws from them for as
     * long as the dialog is open, so releasing one on pick would blank the tile it just added.
     */
    private close(changed: boolean, reviewStatus?: 'accepted' | 'rejected' | 'validated'): void {
        for (const item of this.validationMedia) {
            if (item && item.url) URL.revokeObjectURL(item.url);
        }
        this.validationMedia = [];
        const result: DocumentReviewDialogResult = { changed };
        if (reviewStatus) result.reviewStatus = reviewStatus;
        this.dialogRef.close(result);
    }
}
